import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
  closestCorners,
} from '@dnd-kit/core';
import Avatar from '../components/ui/Avatar';

import { getKanbanBoard, updateStage } from '../api/schedules';
import PipelineColumn       from '../components/pipeline/PipelineColumn';
import PipelineFilters,
       { applyPipelineFilters } from '../components/pipeline/PipelineFilters';
import PipelineDetailDrawer from '../components/pipeline/PipelineDetailDrawer';

const DEFAULT_FILTERS = { search: '', status: '', recommendation: '' };

export default function PipelinePage() {
  const [columns,    setColumns]    = useState([]);   // [{ stage, cards }]
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState(null);
  const [activeCard, setActiveCard] = useState(null);
  const [selected,   setSelected]   = useState(null); // card opened in drawer
  const [filters,    setFilters]    = useState(DEFAULT_FILTERS);

  /** Stage the dragged card was in when drag started (optimistic onDragOver mutates columns before end). */
  const dragOriginStageRef = useRef(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
  );

  // ── Load board ───────────────────────────────────────────────────────────
  const loadBoard = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('[Pipeline] loading kanban board');
      const res = await getKanbanBoard();
      setColumns(res.data || []);
      console.log('[Pipeline] loaded', (res.data || []).length, 'columns');
    } catch (e) {
      console.error('[Pipeline] load failed:', e.message);
      setError(e.response?.data?.message || 'Failed to load pipeline');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadBoard(); }, [loadBoard]);

  // ── Filtering (client-side; the board is small) ──────────────────────────
  const totalCards = useMemo(
    () => columns.reduce((acc, c) => acc + (c.cards?.length || 0), 0),
    [columns],
  );

  const filteredColumns = useMemo(() => columns.map((col) => ({
    ...col,
    cards: applyPipelineFilters(col.cards || [], filters),
  })), [columns, filters]);

  const filteredCount = useMemo(
    () => filteredColumns.reduce((acc, c) => acc + c.cards.length, 0),
    [filteredColumns],
  );

  // ── Helpers ──────────────────────────────────────────────────────────────
  const findColumnByCardId = (cardId) =>
    columns.find(col => col.cards.some(c => c.id === cardId));

  const findCard = (cardId) => {
    for (const col of columns) {
      const card = col.cards.find(c => c.id === cardId);
      if (card) return card;
    }
    return null;
  };

  // ── DnD handlers ─────────────────────────────────────────────────────────
  function handleDragStart({ active }) {
    setActiveCard(findCard(active.id));
    const col = columns.find(c => c.cards.some(card => card.id === active.id));
    dragOriginStageRef.current = col?.stage ?? null;
  }

  function handleDragOver({ active, over }) {
    if (!over) return;

    const activeCol = findColumnByCardId(active.id);
    const overCol =
      findColumnByCardId(over.id) ||
      columns.find(col => col.stage === over.id);

    if (!activeCol || !overCol || activeCol.stage === overCol.stage) return;

    setColumns(prev => prev.map(col => {
      if (col.stage === activeCol.stage) {
        return { ...col, cards: col.cards.filter(c => c.id !== active.id) };
      }
      if (col.stage === overCol.stage) {
        const card = activeCol.cards.find(c => c.id === active.id);
        if (!card) return col;
        return { ...col, cards: [...col.cards, { ...card, stage: overCol.stage }] };
      }
      return col;
    }));
  }

  function handleDragCancel() {
    setActiveCard(null);
    dragOriginStageRef.current = null;
    loadBoard();
  }

  async function handleDragEnd({ active, over }) {
    setActiveCard(null);
    const originStage = dragOriginStageRef.current;
    dragOriginStageRef.current = null;

    if (!over) return;

    // Resolve drop target from `over` only (card id or column droppable id === stage).
    const targetCol =
      findColumnByCardId(over.id) ||
      columns.find(col => col.stage === over.id);

    if (!targetCol) return;

    // After onDragOver, the card already lives in targetCol in state - do not compare
    // findColumnByCardId(active.id) or the PATCH is skipped and the API never runs.
    if (!originStage || originStage === targetCol.stage) return;

    try {
      console.log('[Pipeline] PATCH stage - card:', active.id, '-> stage:', targetCol.stage);
      await updateStage(active.id, targetCol.stage);
    } catch (e) {
      console.error('[Pipeline] stage update failed, reloading board:', e.message);
      loadBoard();
    }
  }

  // ── Render ───────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="page-pad page-y">
        <div className="animate-pulse h-64 rounded-2xl bg-white/60" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-pad page-y">
        <p className="text-red-500 text-sm">{error}</p>
        <button
          onClick={loadBoard}
          className="mt-3 text-brand-600 text-sm font-medium hover:underline"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="page-pad page-y h-full flex flex-col max-w-[1600px] mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-wider text-teal-600">Hiring</p>
          <h1 className="font-heading text-2xl font-bold text-ink tracking-tight">Pipeline</h1>
          <p className="text-sm text-ink-faint mt-0.5">
            Drag candidates between stages · tap a card for details
          </p>
        </div>
        <button
          type="button"
          onClick={loadBoard}
          className="btn btn-sm btn-secondary self-start sm:self-auto rounded-lg border-line"
        >
          <span aria-hidden="true">↺</span>
          <span>Refresh</span>
        </button>
      </div>

      {/* Filters */}
      <PipelineFilters
        value={filters}
        onChange={setFilters}
        totalCards={totalCards}
        filteredCount={filteredCount}
      />

      {/* Board */}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCorners}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
      >
        <div className="flex gap-4 overflow-x-auto pb-4 flex-1 -mx-4 px-4 sm:mx-0 sm:px-0">
          {filteredColumns.map(({ stage, cards }) => (
            <PipelineColumn
              key={stage}
              stage={stage}
              cards={cards}
              activeId={activeCard?.id}
              onCardSelect={setSelected}
            />
          ))}
        </div>

        {/* Floating ghost while dragging */}
        <DragOverlay>
          {activeCard ? (
            <div className="bg-white rounded-xl border border-brand-300 shadow-strong
                            p-3 w-56 rotate-2 opacity-95">
              <div className="flex items-center gap-2">
                <Avatar
                  name={activeCard.candidateName || 'Unknown'}
                  email={activeCard.candidateEmail || ''}
                  size="sm"
                />
                <p className="text-sm font-medium text-slate-800 truncate">
                  {activeCard.candidateName || '-'}
                </p>
              </div>
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>

      {/* Detail drawer */}
      <PipelineDetailDrawer card={selected} onClose={() => setSelected(null)} />
    </div>
  );
}
